import React, { FC, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { getIn, useFormik } from 'formik';
import {
  createPhoneVerificationViaSmsAndUpdatePhone,
} from '../../../../structures/requests/user';
import { TFAFirstStepValidation } from '../validation';
import { addPhoneFormatting, removePhoneFormatting } from '../../../../utils/formatters';

interface Props {
  setStep(step: number): void;
  phoneNumber: string;
  handleClose(): void;
}

interface FormValues {
  phone: string;
}

const TFAFirstStep: FC<Props> = ({ setStep, phoneNumber, handleClose }) => {
  const [apiErrorMessage, setApiErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const {
    handleSubmit, values: formValues, handleBlur, touched, errors, setFieldValue,
  } = useFormik<FormValues>({
    initialValues: {
      phone: addPhoneFormatting(phoneNumber),
    },
    onSubmit: (values) => {
      setLoading(true);
      createPhoneVerificationViaSmsAndUpdatePhone(removePhoneFormatting(values.phone))
        .then(() => {
          setApiErrorMessage(null);
          setStep(2);
        })
        .catch((error) => {
          if (error?.response?.data?.message === 'Phone number already in use') {
            setApiErrorMessage(
              'The phone number is already registered. Login to your account or contact support if you need help to regain access.',
            );
            return;
          }
          setApiErrorMessage(
            `We could not send an SMS to the number: ${values.phone}`,
          );
        }).finally(() => {
          setLoading(false);
        });
    },
    validateOnChange: true,
    validationSchema: TFAFirstStepValidation,
    enableReinitialize: true,
  });

  return (
    <form className="modal-container" onSubmit={handleSubmit}>
      <div className="modal-image">
        <img src="/images/verification-step-1.png" alt="verification-step-1" />
      </div>
      <p className="description-paragraph mb-2">
        For your safety, and that of others,
        we require you to verify your phone number before being able to proceed.
      </p>
      <p className="description-paragraph mb-4">
        Please note that you have to use a mobile phone number - landlines are not accepted.
      </p>

      <Form.Label className="font-weight-bold small-txt">
        Phone number
      </Form.Label>
      <Form.Control
        type="tel"
        name="phone"
        placeholder="Enter phone number"
        value={getIn(formValues, 'phone')}
        isInvalid={getIn(touched, 'phone') && getIn(errors, 'phone')}
        onChange={(e) => setFieldValue('phone', addPhoneFormatting(e.target.value))}
        onBlur={handleBlur}
      />
      {apiErrorMessage ? (
        <p className="text-danger mt-2 text-center">
          {apiErrorMessage}
        </p>
      ) : null}
      <Button
        type="submit"
        variant="primary"
        className="button-base"
        disabled={loading}
      >
        Send me a code
      </Button>
      <Button
        type="button"
        variant="outline-primary"
        onClick={handleClose}
        className="mt-2 button-base"
      >
        Cancel
      </Button>
    </form>
  );
};

export default TFAFirstStep;
